/**
 * Utility to normalize coin image URIs from various feeds (PumpPortal, Pump.fun, etc.)
 * Ensures that IPFS hashes and relative paths are correctly prefixed for display.
 */
export function normalizeCoinImageUri(uri: string | null | undefined, mint?: string) {
  if (!uri && !mint) return undefined;
  
  // If we have a mint but no URI, we can try to guess the Pump.fun image URL
  // because many coins follow the standard pattern:
  // https://images.pump.fun/coin-image/[MINT]?variant=86x86
  if (!uri && mint) {
    return `https://images.pump.fun/coin-image/${mint}?variant=86x86`;
  }

  if (!uri) return undefined;

  // 1. If it's already a full HTTP URL, return it
  if (uri.startsWith('http')) {
    // If it's an IPFS gateway, we might want to swap it for Pump.fun's optimized CDN
    if (uri.includes('ipfs.io/ipfs/') || uri.includes('cf-ipfs.com/ipfs/')) {
       const hash = uri.split('/ipfs/')[1]?.split('?')[0];
       if (hash && mint) {
         return `https://images.pump.fun/coin-image/${mint}?variant=86x86&ipfs=${hash}`;
       }
    }
    return uri;
  }
  
  // 2. Handle IPFS protocol
  if (uri.startsWith('ipfs://')) {
    const hash = uri.replace('ipfs://', '');
    if (mint) {
      return `https://images.pump.fun/coin-image/${mint}?variant=86x86&ipfs=${hash}`;
    }
    return `https://ipfs.io/ipfs/${hash}`;
  }
  
  // 3. Handle raw hashes or relative paths
  if (uri.length > 20) {
    // If it looks like a hash (no extension), assume it's an IPFS hash from Pump.fun
    if (!uri.includes('.')) {
      if (mint) {
        return `https://images.pump.fun/coin-image/${mint}?variant=86x86&ipfs=${uri}`;
      }
      return `https://images.pump.fun/coin-image/${uri}?variant=86x86`;
    }
    // If it's a relative path like "coin-image/..."
    if (uri.startsWith('coin-image/')) {
      return `https://images.pump.fun/${uri}?variant=86x86`;
    }
  }

  // Final Fallback: Try the mint-based CDN URL
  if (mint) {
    return `https://images.pump.fun/coin-image/${mint}?variant=86x86`;
  }

  return uri;
}
